/**
 * Authentication Hook
 * 
 * Handles Supabase authentication for the admin app
 */
import { useState, useEffect } from 'react';
import { createClient, type User } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

let supabaseClient: ReturnType<typeof createClient> | null = null;

function getSupabaseClient() {
  if (!supabaseClient) {
    supabaseClient = createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
        storage: typeof window !== 'undefined' ? window.localStorage : undefined,
        storageKey: 'trangvang-admin-auth',
      },
    });
  }
  return supabaseClient;
}

export interface AuthState {
  user: User | null;
  loading: boolean;
  error: Error | null;
}

/**
 * Hook to get current user and auth state
 */
export function useAuth() {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    loading: true,
    error: null,
  });

  useEffect(() => {
    const supabase = getSupabaseClient();

    // Get initial session
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      setAuthState({
        user: session?.user ?? null,
        loading: false,
        error: error,
      });
    });

    // Listen for auth changes (including token refresh)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      // Handle token refresh events
      if (event === 'TOKEN_REFRESHED' || event === 'SIGNED_IN') {
        setAuthState({
          user: session?.user ?? null,
          loading: false,
          error: null,
        });
      } else if (event === 'SIGNED_OUT') {
        setAuthState({
          user: null,
          loading: false,
          error: null,
        });
      } else {
      setAuthState({
        user: session?.user ?? null,
        loading: false,
        error: null,
      });
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  return authState;
}

/**
 * Hook to check if auth token is ready
 * Useful for enabling queries only when token is available
 */
export function useAuthTokenReady() {
  const { user, loading } = useAuth();
  const [tokenReady, setTokenReady] = useState(false);

  useEffect(() => {
    if (!user || loading) {
      setTokenReady(false);
      return;
    }

    let cancelled = false;

    // Check if token is available - wait a bit for session to be fully loaded
    const checkToken = async () => {
      // Small delay to ensure session is fully loaded from localStorage
      await new Promise(resolve => setTimeout(resolve, 100));
      
      if (cancelled) return;
      
      const token = await getAuthToken();
      if (!cancelled) {
        console.log('useAuthTokenReady: Token check result:', !!token);
        setTokenReady(!!token);
      }
    };

    checkToken();

    return () => {
      cancelled = true;
    };
  }, [user, loading]);

  return tokenReady;
}

/**
 * Get auth token for API requests
 * Automatically refreshes the session if needed
 */
export async function getAuthToken(): Promise<string | null> {
  const supabase = getSupabaseClient();
  
  try {
    // Get current session - wait for it to be ready
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError) {
      console.error('Session error:', sessionError);
      return null;
    }
    
    if (!session) {
      return null;
    }
    
    // Check if token is expired (with 5 minute buffer)
    const expiresAt = session.expires_at;
    if (expiresAt) {
      const expiresIn = expiresAt - Math.floor(Date.now() / 1000);
      // If token expires in less than 5 minutes, refresh it
      if (expiresIn < 300) {
        try {
          const { data: refreshedSession, error: refreshError } = await supabase.auth.refreshSession();
          if (refreshError) {
            console.error('Token refresh error:', refreshError);
            return null;
          }
          if (!refreshedSession?.session) {
            return null;
          }
          return refreshedSession.session.access_token;
        } catch (refreshErr) {
          console.error('Token refresh exception:', refreshErr);
          return null;
        }
      }
    }
    
    return session.access_token ?? null;
  } catch (error) {
    console.error('getAuthToken error:', error);
    return null;
  }
}

/**
 * Sign in with email and password
 */
export async function signIn(email: string, password: string) {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });
  return { data, error };
}

/**
 * Sign out
 */
export async function signOut() {
  const supabase = getSupabaseClient();
  const { error } = await supabase.auth.signOut();
  return { error };
}

